import type { ComponentType } from "react";
import {
  Building2,
  CircleDashed,
  Database,
  HelpCircle,
  MinusCircle,
  ShieldCheck,
  UserPen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Credential, CredentialStatus } from "../types";

type CredentialRequirement = Credential["requirement"];

const STATUS_LABEL: Record<CredentialStatus, string> = {
  verified: "Verified",
  "source-supplied": "External source",
  "provider-supplied": "Provider-supplied",
  "organization-supplied": "Entered by your org",
  missing: "Missing",
  "not-applicable": "N/A",
};

// Never color alone — every status also carries a distinct icon + text
// label. Emerald = primary-source verified, blue = Saile-supplied external
// data, slate = provider-supplied (self-attested, not yet independently
// verified), amber = entered by the organization, red = missing, muted gray =
// N/A.
const STATUS_CLASSES: Record<CredentialStatus, string> = {
  verified: "border-emerald-300 bg-emerald-50 text-emerald-700",
  "source-supplied": "border-blue-300 bg-blue-50 text-blue-700",
  "provider-supplied": "border-slate-300 bg-slate-50 text-slate-600",
  "organization-supplied": "border-amber-300 bg-amber-50 text-amber-700",
  missing: "border-red-300 bg-red-50 text-red-700",
  "not-applicable": "border-gray-200 bg-gray-50 text-gray-400",
};

const STATUS_ICON: Record<
  CredentialStatus,
  ComponentType<{ className?: string }>
> = {
  verified: ShieldCheck,
  "source-supplied": Database,
  "provider-supplied": UserPen,
  "organization-supplied": Building2,
  missing: HelpCircle,
  "not-applicable": MinusCircle,
};

/**
 * Absent, with nobody asking for it.
 *
 * "Missing" is one status covering two different situations, and only one of
 * them is a problem: a required credential that is not there is an unmet
 * obligation, while an optional one is simply not on file. Rendered in the same
 * red as the first, an optional gap read as a demand — and most of the
 * catalogue is optional, so a complete record looked alarming.
 */
const NOT_ON_FILE = {
  label: "Not on file",
  classes: "border-gray-200 bg-gray-50 text-gray-500",
  icon: CircleDashed,
};

/** Primary provenance chip — verified / Saile source / provider-supplied /
 * entered by the organization / missing / N/A.
 * Deliberately separate from the expiry axis (`ExpiryIndicator`): a
 * credential can be both "Verified" AND "Expiring soon" at once. */
export function StatusChip({
  status,
  requirement,
  label,
  className,
}: {
  status: CredentialStatus;
  /** Whether anyone is asking for this credential. Only changes how an absent
   * one reads; every other status means the same thing either way. */
  requirement?: CredentialRequirement;
  /** Server-provided label for external sources. */
  label?: string | null;
  className?: string;
}) {
  const isUnrequestedGap =
    status === "missing" &&
    requirement !== undefined &&
    requirement !== "required";
  const Icon = isUnrequestedGap ? NOT_ON_FILE.icon : STATUS_ICON[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
        isUnrequestedGap ? NOT_ON_FILE.classes : STATUS_CLASSES[status],
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {label ?? (isUnrequestedGap ? NOT_ON_FILE.label : STATUS_LABEL[status])}
    </span>
  );
}
